import {
  AgentMergePresentation_Kind as Kind,
  type AgentMergeEvaluationScope,
  type AgentMergeResolvedStatus,
} from '@github-ui/dotcom-schema/types/copilot_sessions/v1/agent_merge'

import {timestampMillis, type ActivityPresentation} from './agent-merge-activity-reducer'
import {AgentMergeEvaluationScopeError, evaluationScopeKey, validPresentation} from './agent-merge-status-validation'

export const AGENT_MERGE_STATUS_UNAVAILABLE_MESSAGE = 'Agent Merge status is unavailable.'

export class AgentMergeResolvedStatusError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AgentMergeResolvedStatusError'
  }
}

export type ResolvedStatusState = {
  status: AgentMergeResolvedStatus
  deadline?: number
}

export function hydrateResolvedStatus(
  incoming: AgentMergeResolvedStatus,
  expected: AgentMergeEvaluationScope,
): ResolvedStatusState {
  if (!incoming.scope || evaluationScopeKey(incoming.scope) !== evaluationScopeKey(expected)) {
    throw new AgentMergeEvaluationScopeError('Agent Merge resolved status does not match the current authorized scope')
  }
  if (!incoming.visible) return {status: {...incoming, session: undefined}}

  const deadline = incoming.expiresAt ? timestampMillis(incoming.expiresAt) : undefined
  if (
    !validPresentation(incoming.presentation) ||
    (incoming.presentation?.kind === Kind.BUSY && deadline === undefined) ||
    (deadline !== undefined &&
      (!Number.isFinite(deadline) || !validPresentation(incoming.expiredPresentation, false))) ||
    (incoming.expiredPresentation && !validPresentation(incoming.expiredPresentation, false))
  ) {
    throw new AgentMergeResolvedStatusError('Agent Merge resolved status contains an invalid presentation or expiry')
  }

  if (incoming.session?.url) {
    let protocol
    try {
      protocol = new URL(incoming.session.url, 'https://github.com').protocol
    } catch {
      throw new AgentMergeResolvedStatusError('Agent Merge resolved status contains an invalid session URL')
    }
    if (protocol !== 'https:' && protocol !== 'http:') {
      throw new AgentMergeResolvedStatusError('Agent Merge resolved status contains an unsafe session URL')
    }
  }
  return {status: incoming, deadline}
}

export function presentResolvedStatus(
  state: ResolvedStatusState,
  now: number | undefined,
  currentRead: boolean,
): ActivityPresentation {
  const {status} = state
  if (!status.visible) return {row: 'off'}
  if (!currentRead || now === undefined) {
    return {row: 'read_unavailable', attempt: status.scope && evaluationScopeKey(status.scope)}
  }
  const current = state.deadline === undefined || state.deadline > now
  const expired = status.expiredPresentation
  const message = current
    ? status.presentation
    : expired?.code === 'status.unavailable'
      ? {...expired, kind: Kind.WARNING, message: AGENT_MERGE_STATUS_UNAVAILABLE_MESSAGE}
      : expired
  return {
    row: 'baseline',
    message,
    goal: status.session?.goal || undefined,
    url: status.session?.url || undefined,
    job: status.session?.jobId || undefined,
    messageIdentity: JSON.stringify([
      'resolved',
      status.session?.sessionId || status.session?.jobId || (status.scope && evaluationScopeKey(status.scope)),
      message?.kind,
      message?.code,
      message?.message,
    ]),
  }
}
